import React, { useState } from "react"

import { Calendar, Clock, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useCreateDiaryEntry } from "@/hooks/useCreateDiaryEntry"
import { DiaryEntry, Person } from "@/types/person"

interface AddDiaryEntryDialogProps {
  person: Person
  open: boolean
  onOpenChange: (open: boolean) => void
  onEntryCreated?: (newEntry: DiaryEntry) => void
}

const DRIVER_OPS_TAGS = [
  "Onboarding Admin",
  "Off-Boarding",
  "THD Check-in",
  "New Driver Hypercare",
  "Service Review",
  "Serious Escalation",
  "Driver Feedback",
  "No Show Call",
  "Scheduling",
  "Unnotified Absence (UA) Calls",
  "Contractor Ended Engagement",
  "Compliance Violation",
  "General Call",
  "Uniform",
]

const DRIVER_OPS_SUB_TAGS = [
  "Contractor Ended Engagement - Pay Issues",
  "Contractor Ended Engagement - PCN Issues",
  "Contractor Ended Engagement - Routing Issues",
  "Contractor Ended Engagement - Contract Change",
  "Contractor Ended Engagement - Excessive Workload",
  "Contractor Ended Engagement - Block Fulfilment",
  "Contractor Ended Engagement - Vehicle Issues",
  "Contractor Ended Engagement - False Expectations",
  "Contractor Ended Engagement - New Job Found",
  "No Show Call - Tech Issue",
  "No Show Call - No Answer",
  "No Show Call - Sickness",
  "No Show Call - Commute Issues",
  "No Show Call - Other",
  "UA Call - No Answer",
  "UA Call - Sickness",
  "UA Call - Commute Issues",
  "Driver Feedback - Routing issues",
  "Driver Feedback - Pay issues",
  "Driver Feedback - Block issues",
  "Driver Feedback - Improvements",
  "Driver Feedback - Fleet/vehicle issue",
  "Driver Statement",
  "THD New Driver Call",
  "THD Check in - No answer",
  "THD Check-in",
  "Escalation",
  "General Call - Extra Block Call",
  "General Call - New Driver Call",
  "Admin Log",
  "UA Call - Other",
  "Scheduling - Depot Change Request",
  "Service review - Attendance Issue",
  "Service review - Customer Feedback Issues",
  "Service review - Serious Behaviour Issue",
  "Service review - Top Performer",
  "Service review - Abandoned/Refused",
  "Service review - Driving Violation",
  "Service review - Ride Along Request!",
  "Service review - Improved Performance",
  "Service review - Route Efficiency",
  "Service review - Customer Feedback",
  "Service review - Delivery Success",
  "Service review - Compliance Violation",
  "Service Review - Missing Parcels",
  "Service review - Feedback Call",
  "Service review - Feedback Text ",
  "Service review - Protocol/Process issues",
  "Service review - Positive Conduct",
  "Uniform - Hi Vis",
  "Uniform - Safety Boots",
]

const AddDiaryEntryDialog: React.FC<AddDiaryEntryDialogProps> = ({
  person,
  open,
  onOpenChange,
  onEntryCreated,
}) => {
  const createDiaryEntryMutation = useCreateDiaryEntry()
  const isLoading = createDiaryEntryMutation.isPending

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const [formData, setFormData] = useState({
    notes: "",
    driverOpsTag: "",
    driverOpsSubTag: "",
    callDate: "",
    callTime: "",
    followUpAction: "",
    followUpComplete: false,
  })

  const getCurrentDateTime = () => {
    const now = new Date()
    const date = now.toISOString().split("T")[0]
    const time = now.toTimeString().slice(0, 5)
    return { date, time }
  }

  // The tag and sub tag are Radix selects, so the native required attribute cannot gate them
  const missingFields = {
    notes: !formData.notes.trim() ? "Notes are required" : "",
    driverOpsTag: !formData.driverOpsTag ? "Select a driver ops tag" : "",
    driverOpsSubTag: !formData.driverOpsSubTag ? "Select a driver ops sub tag" : "",
    callDateTime: !formData.callDate || !formData.callTime ? "Enter the call date and time" : "",
  }
  const hasRequiredFields = !Object.values(missingFields).some(Boolean)

  const fieldError = (message: string) =>
    hasAttemptedSubmit && message ? (
      <div className="text-xs text-red-600 mt-1">{message}</div>
    ) : null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setHasAttemptedSubmit(true)

    if (!hasRequiredFields) return

    const callDateTime = new Date(`${formData.callDate}T${formData.callTime}`).toISOString()

    // Create diary entry using the hook
    createDiaryEntryMutation.mutate(
      {
        personId: person.id,
        notes: formData.notes,
        driverOpsTag: formData.driverOpsTag,
        driverOpsSubTag: [formData.driverOpsSubTag],
        callDateTime: callDateTime,
        followUpAction: formData.followUpAction || undefined,
        followUpComplete: formData.followUpComplete,
      },
      {
        onSuccess: (data) => {
          // Create a new diary entry object from the form data and response
          const newEntry: DiaryEntry = {
            id: data.id || `entry-${Date.now()}`,
            entryId: data.entryId || `${Date.now()}`,
            content: formData.notes,
            author: data.author || "Current User",
            authorEmail: data.authorEmail || "",
            driverOpsTag: formData.driverOpsTag,
            driverOpsSubTag: [formData.driverOpsSubTag],
            callDateTime: callDateTime,
            linkedToDriver: [person.id],
            followUpAction: formData.followUpAction || "",
            followUpComplete: formData.followUpComplete,
          }

          // Call the callback if provided
          if (onEntryCreated) {
            onEntryCreated(newEntry)
          }

          // Close dialog and reset form on success
          onOpenChange(false)
          setHasAttemptedSubmit(false)
          setFormData({
            notes: "",
            driverOpsTag: "",
            driverOpsSubTag: "",
            callDate: "",
            callTime: "",
            followUpAction: "",
            followUpComplete: false,
          })
        },
      },
    )
  }

  React.useEffect(() => {
    if (open) {
      const { date, time } = getCurrentDateTime()
      setFormData((prev) => ({
        ...prev,
        callDate: date,
        callTime: time,
      }))
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">New Diary Entry</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Call Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Call Details</h3>

            <div className="space-y-2">
              <Label htmlFor="conductedBy">Conducted By</Label>
              <Input
                id="conductedBy"
                value="Current User (Will use your email)"
                readOnly
                className="bg-palette-jungleGreen000 cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">
                Notes <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter detailed notes about the call..."
                className="min-h-[120px]"
                required
              />
              {fieldError(missingFields.notes)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driverOpsTag">
                  Driver Ops Tags <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.driverOpsTag}
                  onValueChange={(value) => {
                    // When tag changes, reset sub tag to avoid mismatches
                    setFormData((prev) => ({
                      ...prev,
                      driverOpsTag: value,
                      driverOpsSubTag: "",
                    }))
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {DRIVER_OPS_TAGS.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError(missingFields.driverOpsTag)}
              </div>

              <div className="space-y-2">
                <Label htmlFor="driverOpsSubTag">
                  Driver Ops Sub Tag <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.driverOpsSubTag}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, driverOpsSubTag: value }))
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Filter sub tags that start with the selected tag when possible */}
                    {DRIVER_OPS_SUB_TAGS.filter(
                      (subTag) =>
                        !formData.driverOpsTag || subTag.startsWith(formData.driverOpsTag) || true,
                    ).map((subTag) => (
                      <SelectItem key={subTag} value={subTag}>
                        {subTag}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError(missingFields.driverOpsSubTag)}
              </div>
            </div>

            {/* Display selected tag and sub tag together for clarity */}
            {formData.driverOpsTag && formData.driverOpsSubTag && (
              <div className="bg-palette-jungleGreen000 p-2 rounded-md text-sm">
                <span className="font-medium">Selected: </span>
                <span className="text-blue-600">{formData.driverOpsTag}</span>
                {formData.driverOpsSubTag && (
                  <>
                    {" "}
                    → <span className="text-green-600">{formData.driverOpsSubTag}</span>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="callDateTime">
                  Call Date/Time <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      type="date"
                      value={formData.callDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          callDate: e.target.value,
                        }))
                      }
                      required
                    />
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-palette-jungleGreen400 pointer-events-none" />
                  </div>
                  <div className="flex-1 relative">
                    <Input
                      type="time"
                      value={formData.callTime}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          callTime: e.target.value,
                        }))
                      }
                      required
                    />
                    <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-palette-jungleGreen400 pointer-events-none" />
                  </div>
                </div>
                {fieldError(missingFields.callDateTime)}
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkToDriver">Link to Driver</Label>
                <Input
                  id="linkToDriver"
                  value={person.person}
                  readOnly
                  className="bg-palette-jungleGreen000 cursor-not-allowed"
                  placeholder="Driver ID"
                  required
                />
              </div>
            </div>
          </div>

          {/* Follow Up Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Follow Up</h3>

            <div className="space-y-2">
              <Label htmlFor="followUpAction">Follow up Action?</Label>
              <Textarea
                id="followUpAction"
                value={formData.followUpAction}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    followUpAction: e.target.value,
                  }))
                }
                placeholder="Enter any follow-up actions required..."
                className="min-h-[100px]"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Entry"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddDiaryEntryDialog
